import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useConsent, OPTIONAL_CATEGORIES, type CookieCategory } from "@/lib/consent";

interface CookieRow {
  id: string;
  name: string;
  vendor: string;
  category: CookieCategory;
  purpose_pt: string;
  purpose_en: string;
  duration: string;
}

const CATEGORY_ORDER: CookieCategory[] = [
  "necessary",
  "functional",
  "analytics",
  "marketing",
  "third_party",
];

export function CookiePreferencesDialog() {
  const { t, i18n } = useTranslation();
  const { managerOpen, closeManager, state, saveCustom, acceptAll } = useConsent();

  const [draft, setDraft] = useState<Record<CookieCategory, boolean>>(() => ({
    necessary: true,
    functional: state?.categories.functional ?? false,
    analytics: state?.categories.analytics ?? false,
    marketing: state?.categories.marketing ?? false,
    third_party: state?.categories.third_party ?? false,
  }));

  // Reset draft when dialog opens (so it reflects latest persisted state).
  useEffect(() => {
    if (managerOpen) {
      setDraft({
        necessary: true,
        functional: state?.categories.functional ?? false,
        analytics: state?.categories.analytics ?? false,
        marketing: state?.categories.marketing ?? false,
        third_party: state?.categories.third_party ?? false,
      });
    }
  }, [managerOpen, state]);

  const { data: cookies = [] } = useQuery({
    queryKey: ["cookie-inventory"],
    queryFn: async (): Promise<CookieRow[]> => {
      const { data, error } = await supabase
        .from("cookie_inventory")
        .select("id,name,vendor,category,purpose_pt,purpose_en,duration")
        .eq("active", true)
        .order("category", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CookieRow[];
    },
    enabled: managerOpen,
    staleTime: 5 * 60 * 1000,
  });

  const isPt = i18n.language?.toLowerCase().startsWith("pt");

  const grouped = CATEGORY_ORDER.reduce<Record<CookieCategory, CookieRow[]>>(
    (acc, c) => {
      acc[c] = cookies.filter((row) => row.category === c);
      return acc;
    },
    {
      necessary: [],
      functional: [],
      analytics: [],
      marketing: [],
      third_party: [],
    },
  );

  return (
    <Dialog open={managerOpen} onOpenChange={(open) => (open ? null : closeManager())}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {t("consent.preferences.title", "Preferências de cookies")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "consent.preferences.description",
              "Escolha quais categorias de cookies você permite. Cookies necessários estão sempre ativos para garantir o funcionamento da plataforma.",
            )}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-3 -mr-3">
          <div className="space-y-3">
            {CATEGORY_ORDER.map((category) => {
              const isLocked = category === "necessary";
              const categoryCookies = grouped[category];
              return (
                <div
                  key={category}
                  className="rounded-xl border border-primary/15 bg-card/50 p-4 space-y-2"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Label
                          htmlFor={`consent-${category}`}
                          className="text-sm font-semibold text-foreground"
                        >
                          {t(`consent.categories.${category}.label`, category)}
                        </Label>
                        {isLocked && (
                          <Badge variant="secondary" className="text-[10px] gap-1">
                            <Lock className="h-3 w-3" aria-hidden="true" />
                            {t("consent.alwaysOn", "Sempre ativo")}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        {t(`consent.categories.${category}.description`, "")}
                      </p>
                    </div>
                    <Switch
                      id={`consent-${category}`}
                      checked={draft[category]}
                      disabled={isLocked}
                      onCheckedChange={(checked) =>
                        setDraft((prev) => ({ ...prev, [category]: checked }))
                      }
                    />
                  </div>

                  {categoryCookies.length > 0 && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-muted-foreground hover:text-primary transition-colors">
                        {t("consent.preferences.viewCookies", "Ver cookies usados")}{" "}
                        ({categoryCookies.length})
                      </summary>
                      <ul className="mt-2 space-y-1.5">
                        {categoryCookies.map((c) => (
                          <li
                            key={c.id}
                            className="rounded-md bg-background/40 px-2 py-1.5"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-mono text-[11px] text-foreground">
                                {c.name}
                              </span>
                              <span className="text-[10px] text-muted-foreground">
                                {c.duration}
                              </span>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5">
                              {c.vendor} — {isPt ? c.purpose_pt : c.purpose_en}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => void acceptAll()}
            className="w-full sm:w-auto"
          >
            {t("consent.preferences.acceptAll", "Aceitar todos")}
          </Button>
          <Button
            onClick={() => void saveCustom(draft)}
            className="w-full sm:w-auto"
          >
            {t("consent.preferences.save", "Salvar preferências")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

void OPTIONAL_CATEGORIES;
